/**
 * What a scrapbook page shows (kid memory design §3.3), apart from React and
 * Relay so `node --test` can reach it.
 */
import { translate, type Locale } from '../i18n/t.ts';
import { startOfDay } from '../views/sessions.ts';
import { isStickerSlug, type StickerSlug } from './stickers.ts';

const DAY_MS = 86_400_000;

/** `format.ts`'s rule: English names no region, so the reader's own is kept. */
function intlTag(locale: Locale): string | undefined {
  return locale === 'en' || locale === 'qps' ? undefined : locale;
}

/**
 * The day in words (§3.3: "Tuesday"), not a date: a child reads a weekday,
 * and "21 Oct" means little at six. A weekday only within the last six days,
 * since a seventh would name today's own weekday; past that, a date.
 * Calendar days, by `startOfDay`, for `whenLabel`'s reason.
 */
export function dayWord(date: Date, now: Date, locale: Locale): string {
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days === 0) return translate(locale, 'session-today');
  if (days === 1) return translate(locale, 'session-yesterday');
  if (days > 1 && days < 7) return date.toLocaleDateString(intlTag(locale), { weekday: 'long' });
  return date.toLocaleDateString(intlTag(locale), { day: 'numeric', month: 'short' });
}

export type PageData = {
  id: string;
  sticker: string;
  createdAt: string;
  sentence?: string | null;
  turnId?: string | null;
  character?: { artKey: string; accentHex: string; intensity: number; displayName: string } | null;
};

export type PageView = {
  id: string;
  sticker: StickerSlug;
  createdAt: Date;
  sentence: string | null;
  /** `GET /audio/{turn_id}`; null hides the hear-it button. */
  audioUrl: string | null;
  character: PageData['character'] | null;
};

export function pageView(page: PageData): PageView {
  const sentence = page.sentence?.trim() || null;
  return {
    id: page.id,
    // A slug from a newer server than this bundle: a page with the plain
    // sticker beats a page with a hole in it.
    sticker: isStickerSlug(page.sticker) ? page.sticker : 'chat',
    createdAt: new Date(page.createdAt),
    sentence,
    audioUrl: sentence && page.turnId ? `/audio/${encodeURIComponent(page.turnId)}` : null,
    character: page.character ?? null,
  };
}

/** The celebration links to `?page=<id>`; a page since gone opens the newest. */
export function startIndex(pages: readonly { id: string }[], id: string | null): number {
  const at = id == null ? -1 : pages.findIndex((p) => p.id === id);
  return at < 0 ? 0 : at;
}

/** Wide enough that a tap on the hear-it button never turns the page. */
const SWIPE_PX = 48;

/** A drag's horizontal distance as a page step: newest first, so leftwards is older. */
export function swipeStep(dx: number): -1 | 0 | 1 {
  if (dx <= -SWIPE_PX) return 1;
  if (dx >= SWIPE_PX) return -1;
  return 0;
}

/**
 * `edge` is whether the arrow just used is now disabled: a focused button
 * that becomes disabled drops focus to `<body>`, so the view moves it to the
 * heading instead (final review).
 */
export function turn(at: number, step: number, count: number): { index: number; edge: boolean } {
  const index = Math.max(0, Math.min(count - 1, at + step));
  return { index, edge: index === 0 || index === count - 1 };
}
